<template>
  <PortalWidget
    :widget="{title: this.$t('HEADER_BULK_TAGS'), iconClass: 'fa fa-tag'}"
  >
    <template v-slot:headerContent>
      <div class="header-extra">
        <input
          v-model="filters.name.value"
          type="search"
          :placeholder="$t('SEARCH_BULK_TAGS')"
          class="form-control input-md widget-header-input m-r-10"
        >
      </div>
    </template>
    <div class="bs-callout bs-callout-primary">
      <i18n path="BULK_TAGS_WARNING" tag="p">
        <template #url>
          <a class="bulk-tags-link" href="https://support.ringba.com/hc/en-us/articles/24340533158551-Bulk-Tags" target="_blank">{{ $t('HEADER_BULK_TAGS') }}</a>
        </template>
      </i18n>
    </div>

    <!-- Table Container -->
    <div
      v-if="!loading"
      class="grid_table_holder"
    >
      <v-table
        :data="bulkTags"
        :current-page.sync="currentPage"
        :filters="filters"
        :page-size="20"
        :hide-sort-icons="true"
        class="table_beauty"
        @totalPagesChanged="totalPages = $event"
      >
        <thead slot="head">
          <tr>
            <th v-if="isMobile" />

            <v-th
              v-t="'TABLE_COL_NAME'"
              default-sort="asc"
              sort-key="name"
            />

            <v-th
              v-t="'TABLE_COL_TAGCOUNT'"
              default-sort="asc"
              sort-key="tagCount"
            />
            <th
              v-if="!isMobile"
              v-t="'TABLE_COL_ACTIONS'"
            />
          </tr>
        </thead>
        <tbody
          slot="body"
          slot-scope="{ displayData }"
        >
          <tr v-if="!bulkTags.length">
            <td
              v-t="'BULK_TAGS_EMPTY'"
              colspan="100"
            />
          </tr>
          <!-- Rows -->
          <template v-for="(row, index) in displayData">
            <tr
              v-if="!(isMobile && activeActionRowId == row.id)"
              :key="row.id"
              :class="{
                'bg-row-odd': index % 2 === 0,
                'bg-row-even': index % 2 !== 0,
              }"
            >
              <!-- Mobile Actions -->
              <td v-if="isMobile">
                <button
                  class="btn btn-list-function btn-expand-actions"
                  @click="displayMobileAction(row.id)"
                >
                  <i class="fa fa-ellipsis-v" />
                </button>
              </td>
              <!-- Name -->
              <td>
                {{ row.name }}
              </td>

              <!-- Type -->
              <td>
                {{ row.tagCount }}
              </td>

              <!-- Desktop Actions -->
              <td v-if="!isMobile">
                <BulkTagsActions
                  :current-bulk-tag="row"
                  :disabled="disabled"
                  @deleteBulkTags="confirmDeleteBulkTags"
                  @downloadBulkTags="downloadBulkTags"
                  @showBulkTagsReferences="displayReferences"
                />
              </td>
            </tr>

            <tr
              v-if="isMobile && activeActionRowId == row.id"
              :key="row.id + '-mobile'"
              class="mobile-actions slide-right"
            >
              <td colspan="10">
                <BulkTagsActions
                  :current-bulk-tag="row"
                  :disabled="disabled"
                  @deleteBulkTags="confirmDeleteBulkTags"
                  @collapseActions="!disabled && hideMobileAction()"
                  @downloadBulkTags="downloadBulkTags"
                  @showBulkTagsReferences="displayReferences"
                />
              </td>
            </tr>
            <!-- References -->
            <tr
              v-if="showReferences(row.id)"
              :key="row.id + '-references'"
              class="row-inline-new-form slide-down bg-row-even"
            >
              <td colspan="100">
                <div class="p-l-15 p-r-15">
                  <legend class="f-s-13 p-b-10 p-l-0">
                    <span v-t="'REFERENCES'" />
                    <button
                      type="button"
                      class="btn btn-xs btn-danger legend-right-button"
                      @click="hideReferences"
                    >
                      <i
                        aria-hidden="true"
                        class="fa fa-close"
                      />
                      Close
                    </button>
                  </legend>
                  <BulkTagsReferences
                    :bulk-tag-upload-id="row.id"
                  />
                </div>
              </td>
            </tr>
            <!-- References -->
          </template>
        </tbody>
      </v-table>
      <div
        v-if="bulkTags && bulkTags.length"
        class="pagination-min"
      >
        <div class="text-center p-7">
          <smart-pagination
            :current-page.sync="currentPage"
            :total-pages="totalPages"
            :max-page-links="7"
          />
        </div>
      </div>
    </div>
    <div
      v-else
      class="text-center empty-records"
    >
      <Loader />
      <span v-t="'BULK_TAGS_TABLE_LOADING'" />
    </div>
    <BulkTagsDeleteModal
      v-if="showConfirmation"
      :bulk-tags="bulkTagsToDelete"
      :disable-modal-confirm="disableModalConfirm"
      :deleting-bulk-tags="deletingBulkTags"
      :references="references"
      :has-references="hasReferences"
      @closeModal="closeModal"
      @attestDelete="attestDelete"
      @deleteBulkTags="callDeleteBulkTags"
    />
  </PortalWidget>
</template>

<script>
import messages from '../../locales';
import sharedMessages from '@/core/locales';
import { isMobile } from '@/core/utilities';
import PortalWidget from '@/core/components/PortalWidget.vue';
import Loader from '@/core/components/Loader';
import BulkTagsActions from './BulkTagsActions';
import BulkTagsDeleteModal from './BulkTagsDeleteModal';
import BulkTagsReferences from './BulkTagsReferences';
// import BulkTagsDeleteModalBasic from './BulkTagsDeleteModalBasic';

import { utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { mapActions, mapState, mapGetters } from 'vuex';
import { STATUS_ERROR, STATUS_SUCCESS, STATUS_PENDING } from '@/store/helpers';
import * as actionTypes from '@/modules/bulkTags/store/actionTypes';

export default {
  components: {
    PortalWidget,
    Loader,
    BulkTagsActions,
    BulkTagsReferences,
    BulkTagsDeleteModal
  },
  data() {
    return {
      currentPage: 1,
      totalPages: 0,
      isMobile: isMobile,
      activeActionRowId: null,
      activeReferencesRowId: null,
      bulkTagsToDelete: null,
      bulkTagsDeleteForce: false,
      showConfirmation: false,
      filters: {
        name: { value: '', keys: ['name', 'id'] }
      },
    };
  },
  computed: {
    ...mapState('bulkTags', [
      'bulkTags',
      'bulkTagsUrl',
      'references'
    ]),
    ...mapGetters('bulkTags', [
      'getBulkTagsStatus',
      'deleteBulkTagsStatus',
      'getBulkTagsReferencesStatus',
      'getBulkTagsUrlStatus'
    ]),
    loading() {
      return this.getBulkTagsStatus.status == STATUS_PENDING;
    },
    deletingBulkTags() {
      return this.deleteBulkTagsStatus.status === STATUS_PENDING;
    },
    disabled() {
      return !!this.activeReferencesRowId;
    },
    hasReferences() {
      return this.references && this.references.length > 0;
    },
    disableModalConfirm() {
      return (
        this.getBulkTagsReferencesStatus.status == STATUS_PENDING ||
        this.getBulkTagsReferencesStatus.status == STATUS_ERROR ||
        this.deletingBulkTags ||
        !this.bulkTagsDeleteForce
      );
    },
  },
  watch: {
    currentPage() {
      this.hideReferences();
    },
    filters: {
      immediate: true,
      deep: true,
      handler() {
        this.hideReferences();
      }
    }
  },
  async created() {
    this.getBulkTags();
  },
  methods: {
    ...mapActions('bulkTags', Object.values(actionTypes)),
    displayMobileAction(id) {
      this.activeActionRowId = id;
    },
    hideMobileAction() {
      this.activeActionRowId = null;
    },
    displayReferences(id) {
      this.activeReferencesRowId = id;
    },
    showReferences(id) {
      return this.activeReferencesRowId == id;
    },
    hideReferences() {
      this.activeReferencesRowId = null;
    },
    closeModal() {
      this.showConfirmation = false;
      this.bulkTagsDeleteForce = false;
    },
    attestDelete(value) {
      this.bulkTagsDeleteForce = value;
    },
    async confirmDeleteBulkTags(bulkTag) {
      this.bulkTagsToDelete = bulkTag;
      this.showConfirmation = true;
      await this.getBulkTagsReferences(bulkTag.id);
      if (this.getBulkTagsReferencesStatus.status === STATUS_SUCCESS) {
        if(this.references.length == 0) { this.bulkTagsDeleteForce = true; }
      }
    },
    async callDeleteBulkTags(bulkTag) {
      await this.deleteBulkTags(bulkTag.id);
      if (this.deleteBulkTagsStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(
          STATUS_SUCCESS,
          this.$i18n.t('MESSAGE_DELETE_SUCCESS')
        );
        this.getBulkTags();
      }
      if (this.deleteBulkTagsStatus.status === STATUS_ERROR) {
        utilities.showToaster(
          STATUS_ERROR,
          this.deleteBulkTagsStatus.error
        );
      }
      this.bulkTagsToDelete = null;
      this.showConfirmation = false;
      this.bulkTagsDeleteForce = false;
    },
    async downloadBulkTags(bulkTag) {
      await this.getBulkTagsUrl(bulkTag.id);
      if (this.getBulkTagsUrlStatus.status === STATUS_SUCCESS) {
        await this.downloadBulkTagsFile(bulkTag);
      }
      if (this.getBulkTagsUrlStatus.status === STATUS_ERROR) {
        utilities.showToaster(
          STATUS_ERROR,
          this.getBulkTagsUrlStatus.error
        );
      }
    },
    async downloadBulkTagsFile(bulkTag) {
      const url = this.bulkTagsUrl;

      const aElement = document.createElement('a');
      aElement.setAttribute('download', bulkTag.name);
      aElement.href = url;
      aElement.setAttribute('target', '_blank');
      aElement.click();
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en',
  },

};
</script>
<style lang="css" scoped>
.bulk-tags-link{
  font-weight: bold;
  text-decoration: underline;
}
</style>
